import { useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  DATE_VISIBILITY_OPTIONS,
  coerceDateVisibility,
  type DateVisibility,
} from '@/components/forms/date-visibility';

function resolveOption(value?: string | null) {
  return DATE_VISIBILITY_OPTIONS.find((o) => o.value === value) || DATE_VISIBILITY_OPTIONS[0];
}

interface DateVisibilityPickerProps {
  value?: string | null;
  onChange: (value: DateVisibility) => void;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  /** When true, closed trigger shows only the icon; menu items stay full. */
  iconOnly?: boolean;
  'aria-label'?: string;
}

export function DateVisibilityPicker({
  value,
  onChange,
  disabled,
  className,
  triggerClassName,
  iconOnly = true,
  'aria-label': ariaLabel = 'Visibility',
}: DateVisibilityPickerProps) {
  const [open, setOpen] = useState(false);
  const selected = resolveOption(value);
  const SelectedIcon = selected.icon;

  return (
    <div className={cn(className)}>
      <Select
        open={open}
        onOpenChange={setOpen}
        value={(value as DateVisibility) || 'PUBLIC'}
        onValueChange={(next) => onChange(next as DateVisibility)}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={`${ariaLabel}: ${selected.label}`}
          title={selected.label}
          className={cn(
            'rounded-xl border-border bg-background',
            iconOnly
              ? 'h-9 w-9 shrink-0 justify-center gap-0 px-0 [&>svg]:hidden'
              : 'h-10 min-w-[9.5rem]',
            open && !iconOnly && 'min-w-[11rem]',
            triggerClassName
          )}
        >
          <SelectValue>
            <span
              className={cn(
                'flex items-center text-sm font-semibold text-foreground',
                iconOnly ? 'gap-0' : 'gap-2'
              )}
            >
              <SelectedIcon className="h-4 w-4 shrink-0 text-primary" />
              {!iconOnly ? selected.label : null}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="rounded-xl min-w-[12rem]">
          {DATE_VISIBILITY_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <SelectItem key={option.value} value={option.value} className="rounded-lg">
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-primary" />
                  {option.label}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

interface DateVisibilitySelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  className?: string;
  triggerClassName?: string;
  iconOnly?: boolean;
  showLabel?: boolean;
  /** Hide inline error text (use for icon-only eyes beside inputs). */
  hideMessage?: boolean;
}

export function DateVisibilitySelect<TFieldValues extends FieldValues>({
  control,
  name,
  className,
  triggerClassName,
  iconOnly = true,
  showLabel = false,
  hideMessage = false,
}: DateVisibilitySelectProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = coerceDateVisibility(field.value as string | null | undefined);
        return (
          <FormItem className={cn(showLabel ? 'space-y-1.5' : 'space-y-0', className)}>
            {showLabel ? (
              <FormLabel className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                Visibility
              </FormLabel>
            ) : null}
            <FormControl>
              <DateVisibilityPicker
                value={value}
                onChange={(next) => field.onChange(coerceDateVisibility(next))}
                iconOnly={iconOnly}
                triggerClassName={triggerClassName}
                aria-label="Visibility"
              />
            </FormControl>
            {hideMessage || iconOnly ? null : <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}
